'use client';

import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import AIChatMode from './AIChatMode';
import ManualMode, { ManualModeRef, ArticlePlanningData, ManualModeStep } from './ManualMode';

interface CreateArticleDialogProps {
  mode: 'chat' | 'manual';
  onComplete: (data: ArticlePlanningData) => void; // For manual form completion
  onChatComplete: (data: any) => void; // For AI chat completion
  onClose: () => void;
  initialData?: ArticlePlanningData;
  onStepChange?: (step: ManualModeStep) => void;
  manualModeStep?: ManualModeStep;
}


export interface CreateArticleDialogRef {
  resetManualModeGeneratingState: () => void;
  submitForm: () => void;
  triggerValidation: () => Promise<boolean>;
}

const CreateArticleDialog = forwardRef<CreateArticleDialogRef, CreateArticleDialogProps>(
  ({ mode, onComplete, onChatComplete, onClose, initialData, onStepChange, manualModeStep }, ref) => {
    const manualModeRef = useRef<ManualModeRef>(null);

    useImperativeHandle(ref, () => ({
      triggerValidation: async () => {
        if (manualModeRef.current) {
          return await manualModeRef.current.triggerValidation();
        }
        return false;
      },
      resetManualModeGeneratingState: () => {
        manualModeRef.current?.resetGeneratingState();
      },
      submitForm: () => {
        manualModeRef.current?.submit();
      },
    }));

    const handleManualFormComplete = (data: ArticlePlanningData) => {
      onComplete(data);
    };

    // This now calls the new, dedicated prop for chat completion.
    const handleChatComplete = (data: any) => {
      console.log('Chat completed with data:', data);
      onChatComplete(data);
    };

    return (
      <>
        {mode === 'chat' ? (
          <AIChatMode onComplete={handleChatComplete} />
        ) : (
          <ManualMode
            ref={manualModeRef}
            onComplete={handleManualFormComplete}
            initialData={initialData}
            onStepChange={onStepChange}
            currentStep={manualModeStep}
          />
        )}
      </>

    );
  }
);

CreateArticleDialog.displayName = 'CreateArticleDialog';

export default CreateArticleDialog;
